import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutGrid, ChevronDown, Save } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import { ConfirmModal } from '../ui/Modal';
import OsScroll from '../ui/OsScroll';
import { useTerminalConfigStore } from '../../store/terminalConfigStore';
import LayoutPresetGrid from './LayoutPresetGrid';
import SavedLayoutRow from './SavedLayoutRow';
import SaveLayoutModal from './SaveLayoutModal';
import { applyPresetPlan, openSavedLayout, planPreset } from './layoutActions';
import './TerminalMenu.css';

/**
 * Layouts ▾ in the tab toolbar. PRESETS reshape the CURRENT tab (running
 * panes keep their place in reading order; extra leaves get the default
 * shell; if the preset has fewer leaves, a confirm lists what would close).
 * SAVED layouts open in a NEW tab and never touch running panes. Rename
 * inline, delete with Undo, "Save current layout…".
 */
export default function TerminalLayoutsMenu({ tab }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [confirm, setConfirm] = useState(null); // { plan, preset }
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);
  const layouts = useTerminalConfigStore((s) => s.layouts);
  const profiles = useTerminalConfigStore((s) => s.profiles);

  useEffect(() => {
    useTerminalConfigStore.getState().load();
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape' && !renaming) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, renaming]);

  const applyPreset = (preset) => {
    const plan = planPreset(tab.id, preset.id);
    if (!plan) return;
    setOpen(false);
    if (plan.closing.length) setConfirm({ plan, preset });
    else applyPresetPlan(tab.id, plan);
  };

  const openSaved = (layout) => {
    setOpen(false);
    openSavedLayout(layout);
  };

  const presetsDisabled = !!tab.locked;

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <Tooltip content={t('terminalExt.layouts', 'Layouts')} side="bottom">
        <button
          type="button"
          className="term-tool-btn term-tool-btn--wide"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={t('terminalExt.layouts', 'Layouts')}
          onClick={() => setOpen((v) => !v)}
        >
          <LayoutGrid size={15} />
          <ChevronDown size={11} />
        </button>
      </Tooltip>
      {open && (
        <div className="term-menu term-layouts-menu absolute mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden" style={{ top: '100%', right: 0 }} role="menu">
          <OsScroll className="term-menu-list">
            <div className="term-menu-list-inner">
              <div className="term-menu-section">
                {t('terminalExt.presets', 'Presets')}
                <span className="term-menu-section-note">{t('terminalExt.presetsNote', 'reshape this tab')}</span>
              </div>
              <LayoutPresetGrid disabled={presetsDisabled} onApply={applyPreset} />
              <div className="term-menu-section">
                {t('terminalExt.savedLayouts', 'Saved')}
                <span className="term-menu-section-note">{t('terminalExt.savedNote', 'open in a new tab')}</span>
              </div>
              {layouts.length === 0 && <div className="term-menu-empty">{t('terminalExt.noSavedLayouts', 'Save a tab’s panes to reopen them here in one click.')}</div>}
              {layouts.map((l) => (
                <SavedLayoutRow key={l.id} layout={l} profiles={profiles} renaming={renaming} setRenaming={setRenaming} onOpen={openSaved} />
              ))}
            </div>
          </OsScroll>
          <div className="term-menu-footer">
            <button
              type="button"
              className="term-menu-foot-btn"
              disabled={!!tab.locked}
              onClick={() => {
                setOpen(false);
                setSaving(true);
              }}
            >
              <Save size={13} aria-hidden />
              {t('terminalExt.saveLayout', 'Save layout…')}
            </button>
          </div>
        </div>
      )}
      {saving && <SaveLayoutModal tabId={tab.id} onClose={() => setSaving(false)} />}
      <ConfirmModal
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          applyPresetPlan(tab.id, confirm.plan);
          setConfirm(null);
        }}
        title={t('terminalExt.closePanesTitle', 'Apply “{{name}}”?', { name: confirm ? t(confirm.preset.labelKey, confirm.preset.label) : '' })}
        message={t('terminalExt.closePanesConfirm', 'This closes {{count}} pane(s): {{names}}. Their programs end.', {
          count: confirm?.plan.closing.length || 0,
          names: (confirm?.plan.closing || []).map((p) => p.title || p.label).join(', '),
        })}
        confirmText={t('terminalExt.applyLayout', 'Apply')}
        type="danger"
      />
    </div>
  );
}
